// index.ts — entry point for the player-character-sheet bundle (IIFE →
// includes/js/dist/player-character-sheet-bundle.js). Client-side renderer for the
// PC sheet's tab panes, fed from window.FateTide.casterCache (seeded by item.php,
// kept fresh by the caster_update websocket). First ported pane: Defenses.
//
// MOUNT CONTRACT — PHP emits, per ported pane:
//   <div class="ft-pc-sheet-pane" data-ft-pc-pane="defenses"
//        data-ft-caster-id="<encounter_creature_id>"></div>
// The scanner pulls the caster blob by id and paints the pane (object-card pattern:
// JSON already in the page cache, no fetch on the happy path; async fetch fallback
// on a cold cache).
//
// SURGICAL UPDATE — caster-update.ts calls window.ftRerenderPcSheet(casterId) after
// it refreshes the cache; we re-render only the mounted panes for that caster from
// the new blob. No full-sheet AJAX, so scroll / sub-tab / focus survive.

import { displayRefusal } from './k5-runtime';
import { ftActionBarSave } from './tabs/action-bar';
import { displayCasterFromCache as getCaster, fetchDisplayCaster, mountCreatureStats } from './display-caster-cache';
import { renderPcSheetPane } from './render-pc-sheet';
import { patchActionBarPreferredSpeed, patchActionBarResources } from './tabs/action-bar';
import { hydrateAbilityChargeHints } from './ability-button';
import { casterPaneAffected, casterResourcesOnly, casterScalarPaths } from '../shared/caster-view-changes';
import { patchSkillGrantRows } from './tabs/skills';
import './target-success-preview';
import './ability-choice';
import './creature-heartbeat';
import { sheetCastablePayload } from './creature-selectors';
import { ftRenderInventoryWeightBreakdown } from './tabs/weight-breakdown';
import { patchActionBarSpells } from './tabs/action-bar';
import { patchCastableSpellRows } from './tabs/castable-spells';
import { ftActionBarAddRow, ftLegacyAbilityClickAs, ftActionBarRemoveRow, ftActionBarToggleLock, ftActionBarAddDivider, ftActionBarEquipInventory, ftActionBarSelectAbilityCategory, ftActionBarSelectSkillAbilitySource, ftActionBarIslandSort, ftActionBarPivotMechanic, ftActionBarToggleSettings, ftActionBarToggleBindMode, ftActionBarBindSlot, ftActionBarSetTheme, ftActionBarSetCustomColor, ftActionBarChoiceCancel, ftActionBarChoiceConfirm, ftActionBarToggleRitual, ftActionBarAoeUpcastStep, ftActionBarArmAoeChoice, ftActionBarSetAoeTargets, ftActionBarTopTab, ftAbChoiceStep, ftActionBarToggleSkills, ftActionBarSkillType, ftActionBarSkillsShowAll, ftUpdateActivityBars, ftActionBarWeaponFoldFire, ftActionBarWeaponFoldBasic, ftActionBarWeaponFoldClose, ftActionBarDismissOverlays, ftDispatchAbilityClick, ftDispatchAbilityVariantClick, ftActionBarSelectUnit, ftActionBarCycleUnit } from './tabs/action-bar';
import { renderClassMechanicInfoPanel } from './tabs/weapon-abilities';
import type { CachedCaster } from './types';
import { ftPaintTradeStock } from './tabs/inventory';
import { renderLegacyAbilityButton, type LegacyAbilityButtonInput } from './ability-button';

const PANE_SELECTOR = '.ft-pc-sheet-pane';
const DONE_ATTR = 'data-ft-pc-done';
const dirtyPanes = new Set<HTMLElement>();
const inactivePane = (el: HTMLElement): boolean => !!el.closest?.('.tab-pane:not(.active), [hidden]');
const buttonMounts = new WeakMap<HTMLElement, { input: LegacyAbilityButtonInput; key?: string | null; pending: boolean; done: boolean;
    html?: string; first?: ChildNode | null }>();

/** Current compiled caster/ability, shared native rows, and the existing delegated click lifecycle. */
export function ftMountAbilityButtons(root: ParentNode = document, casterId?: string): boolean {
    const selector = 'ft-ability-button';
    const mounts = [...root.querySelectorAll<HTMLElement>(selector)];
    if (root instanceof HTMLElement && root.matches(selector)) mounts.unshift(root);
    let handled = false;
    for (const el of mounts) {
        const previous = buttonMounts.get(el);
        const text = el.querySelector('.ft-ability-button-input')?.textContent;
        const changed = !!text && text !== previous?.key;
        const input = changed ? JSON.parse(text) as LegacyAbilityButtonInput : previous?.input;
        if (!input || (casterId && input.caster_id.toUpperCase() !== casterId.toUpperCase())) continue;
        handled = true;
        if (!changed && (previous?.pending || (previous?.done && !casterId))) continue;
        const state = { input, key: text ?? previous?.key, pending: true, done: false };
        buttonMounts.set(el, state);
        el.dataset.ftCasterId = input.caster_id;
        const paint = async (): Promise<void> => {
            const caster = getCaster(input.caster_id) ?? await fetchDisplayCaster(input.caster_id);
            if (!el.isConnected || buttonMounts.get(el) !== state) return;
            let html: string;
            // A nested ability can be outside the caster catalog; the normal object-cache
            // reader adopts its raw envelope through the page's compiler before painting.
            try { html = renderLegacyAbilityButton(input, caster); }
            catch (error) {
                if (!String(error).includes('unported:ability-definition:not-held:') || !window.fetchAndCacheObject) throw error;
                await window.fetchAndCacheObject(input.ability_id);
                html = renderLegacyAbilityButton(input, caster);
            }
            if (!el.isConnected || buttonMounts.get(el) !== state) return;
            // The same button markup keeps the painted button, its selects and their picks (rebuild audit 2026-10-25).
            if (previous?.html === html && previous.first && previous.first === el.firstChild) {
                Object.assign(state, { html, first: previous.first, done: true });
                return;
            }
            const picks = new Map([...el.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[id^="input_"]')].map(field => [field.id, field.value]));
            el.querySelectorAll<HTMLSelectElement>('select').forEach(select => (select as HTMLSelectElement & { tomselect?: { destroy(): void } }).tomselect?.destroy());
            el.innerHTML = html;
            el.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[id^="input_"]').forEach(field => {
                const value = picks.get(field.id);
                if (value !== undefined) field.value = value;
            });
            Object.assign(state, { html, first: el.firstChild, done: true });
            window.ftMountRuneIcons?.(el);
            window.initializeTomSelect?.();
        };
        void paint().catch(error => { if (el.isConnected) el.innerHTML = displayRefusal(error); })
            .finally(() => { state.pending = false; });
    }
    return handled;
}

/** UUID caster ids are attribute-selector-safe except for quotes; strip them defensively. */
function attrSafe(id: string): string {
    return String(id).replace(/["\\]/g, '');
}

function paintPane(el: HTMLElement, caster: CachedCaster): boolean {
    const paneName = el.getAttribute('data-ft-pc-pane') || '';
    const casterId = el.getAttribute('data-ft-caster-id') || '';
    if (!paneName || !casterId) return false;
    if (renderPcSheetPane(paneName, caster, casterId, el)) {
        dirtyPanes.delete(el);
        el.setAttribute(DONE_ATTR, '1');
        return true;
    }
    return false;
}

function mountPane(el: HTMLElement): void {
    if (el.getAttribute(DONE_ATTR) === '1') return;
    if (inactivePane(el)) { dirtyPanes.add(el); return; }
    const casterId = el.getAttribute('data-ft-caster-id') || '';
    if (!casterId) return;

    const caster = getCaster(casterId);
    if (caster) {
        paintPane(el, caster);
        return;
    }
    // Cold cache (e.g. AJAX-injected sheet without a fresh seed): fetch then paint.
    void fetchDisplayCaster(casterId).then(fresh => paintPane(el, fresh))
        .catch(error => { el.innerHTML = displayRefusal(error); });
}

/** Scan a root for un-mounted PC-sheet panes and paint them. */
/** A preparation edit invalidates spell surfaces only; the preparation table listens to its saved-truth event. */
function ftPatchPcSpellPanes(casterId: string, spellIds: readonly string[]): void {
    const caster = getCaster(casterId);
    if (!caster) return;
    document.querySelectorAll<HTMLElement>(`${PANE_SELECTOR}[data-ft-caster-id="${attrSafe(casterId)}"]`).forEach(el => {
        if (el.dataset.ftPcPane === 'castable_spells') patchCastableSpellRows(caster, casterId, el, spellIds);
        if (el.dataset.ftPcPane === 'action_bar') patchActionBarSpells(caster, casterId, el);
    });
}
declare global { interface Window { ftPatchPcSpellPanes?: typeof ftPatchPcSpellPanes } }
window.ftPatchPcSpellPanes = ftPatchPcSpellPanes;

function ftMountPcSheet(root: ParentNode = document): void {
    mountCreatureStats(root);
    ftMountAbilityButtons(root);
    root.querySelectorAll<HTMLElement>('.ft-caster-card-mount:not([data-ft-card-mounted])').forEach(el => {
        const id = el.dataset.ftCasterId ?? '';
        if (!id) return;
        el.dataset.ftCardMounted = 'loading';
        const paint = (): void => {
            const render = (window as unknown as { ftRenderCasterCardFromCache?: (id: string, el: HTMLElement, options: Record<string, unknown>) => boolean }).ftRenderCasterCardFromCache;
            const ok = render?.(id, el, { sideInfo: el.dataset.ftSideInfo === '1', cardColClasses: el.dataset.ftCardColumns, viewTarget: id,
                viewerCanControl: el.dataset.ftViewerCanControl === '1', viewerCasterId: el.dataset.ftViewerCasterId,
                readOnlyInspect: el.dataset.ftViewerCanControl !== '1' });
            el.dataset.ftCardMounted = ok ? '1' : 'refused';
            if (!ok) el.innerHTML = displayRefusal('unported:creature-display:card-context-not-supported');
        };
        if (getCaster(id)) paint();
        else void fetchDisplayCaster(id).then(paint).catch(error => { el.dataset.ftCardMounted = 'refused'; el.innerHTML = displayRefusal(error); });
    });
    root.querySelectorAll<HTMLElement>(`${PANE_SELECTOR}:not([${DONE_ATTR}="1"])`).forEach(mountPane);
}

/**
 * Refresh only panes subscribed to the committed paths; inactive tabs catch up on activation.
 * Omitted paths mean a full/new snapshot. The return value still reports a mounted TS sheet.
 */
function ftRerenderPcSheet(casterId: string, changedPaths?: readonly string[]): boolean {
    if (!changedPaths || changedPaths.some(path => /^(permissions|viewer)(\.|$)/.test(path)))
        (window as unknown as { ftRefreshCreatureCardAccess?: (id: string) => void }).ftRefreshCreatureCardAccess?.(casterId);
    const scalar = changedPaths && casterScalarPaths(changedPaths);
    const resources = changedPaths && casterResourcesOnly(changedPaths);
    const preferredSpeed = changedPaths?.length === 1 && changedPaths[0] === 'encounter_creatures.preferred_speed_type';
    if (!scalar && !resources && !preferredSpeed) mountCreatureStats(document);
    const buttonsHandled = scalar || resources || preferredSpeed ? !!document.querySelector(`ft-ability-button[data-ft-caster-id="${attrSafe(casterId)}"]`)
        : ftMountAbilityButtons(document, casterId);
    const panes = document.querySelectorAll<HTMLElement>(
        `${PANE_SELECTOR}[data-ft-caster-id="${attrSafe(casterId)}"]`,
    );
    if (panes.length === 0) {
        if (changedPaths && !preferredSpeed && (!scalar || resources)) hydrateAbilityChargeHints(document, casterId);
        return buttonsHandled;
    }
    const caster = getCaster(casterId);
    if (!caster) return true; // sheet is present but cache not ready; still "handled"
    panes.forEach((el) => {
        const name = el.getAttribute('data-ft-pc-pane') || '';
        if (!casterPaneAffected(name, changedPaths)) return;
        if (inactivePane(el)) { dirtyPanes.add(el); return; }
        const elevation = changedPaths?.some(path => path === 'position' || path === 'position.z_pos' || path === 'encounter_creatures.z_pos');
        if (preferredSpeed && name === 'action_bar' && el.getAttribute(DONE_ATTR) === '1') patchActionBarPreferredSpeed(caster, el);
        else if (resources && !elevation && name === 'action_bar' && el.getAttribute(DONE_ATTR) === '1') patchActionBarResources(caster, casterId, el);
        else paintPane(el, caster);
    });
    if (changedPaths && !preferredSpeed && (!scalar || resources)) hydrateAbilityChargeHints(document, casterId);
    return true;
}

function refreshVisiblePanes(): void {
    for (const el of dirtyPanes) {
        if (!el.isConnected) { dirtyPanes.delete(el); continue; }
        if (inactivePane(el)) continue;
        const caster = getCaster(el.getAttribute('data-ft-caster-id') || '');
        if (caster) paintPane(el, caster); else mountPane(el);
    }
}
document.addEventListener('shown.bs.tab', refreshVisiblePanes);
document.addEventListener('shown.bs.tab', () => hydrateAbilityChargeHints());
document.addEventListener('click', () => queueMicrotask(refreshVisiblePanes));
// B-key dock reveal changes only `hidden`, without a click or Bootstrap tab event.
new MutationObserver(records => {
    if (records.some(record => record.target instanceof Element && record.target.matches('[data-ft3d-actor-dock]:not([hidden])'))) {
        refreshVisiblePanes();
        hydrateAbilityChargeHints();
    }
}).observe(document.documentElement, { attributes: true, attributeFilter: ['hidden'], subtree: true });

Object.assign(window, { ftActionBarSave });
Object.assign(window, { ftPatchSkillGrantRows: (id: string, variables: readonly string[]) => {
    const caster = getCaster(id);
    if (!caster) return;
    for (const pane of document.querySelectorAll<HTMLElement>('.ft-pc-sheet-pane[data-ft-pc-pane="skills"]'))
        if (pane.dataset.ftCasterId?.toUpperCase() === id.toUpperCase()) patchSkillGrantRows(caster, id, pane, variables);
} });

Object.assign(window, { ftCreatureCastablePayload: sheetCastablePayload, ftMountPcSheet, ftLegacyAbilityClickAs, ftRerenderPcSheet, renderPcSheetPane, ftRenderInventoryWeightBreakdown, ftActionBarAddRow, ftActionBarRemoveRow, ftActionBarToggleLock, ftActionBarAddDivider, ftActionBarEquipInventory, ftActionBarSelectAbilityCategory, ftActionBarSelectSkillAbilitySource, ftActionBarIslandSort, ftActionBarPivotMechanic, ftActionBarToggleSettings, ftActionBarToggleBindMode, ftActionBarBindSlot, ftActionBarSetTheme, ftActionBarSetCustomColor, ftActionBarChoiceCancel, ftActionBarChoiceConfirm, ftActionBarToggleRitual, ftActionBarAoeUpcastStep, ftActionBarArmAoeChoice, ftActionBarSetAoeTargets, ftActionBarTopTab, ftAbChoiceStep, ftActionBarToggleSkills, ftActionBarSkillType, ftActionBarSkillsShowAll, ftUpdateActivityBars, ftActionBarWeaponFoldFire, ftActionBarWeaponFoldBasic, ftActionBarWeaponFoldClose, ftActionBarDismissOverlays, ftDispatchAbilityClick, ftDispatchAbilityVariantClick, ftActionBarSelectUnit, ftActionBarCycleUnit, renderClassMechanicInfoPanel });

Object.assign(window, { ftPaintTradeStock });
Object.assign(window, { ftMountAbilityButtons });

if (typeof customElements !== 'undefined' && !customElements.get('ft-ability-button')) {
    customElements.define('ft-ability-button', class extends HTMLElement {
        connectedCallback(): void { queueMicrotask(() => ftMountAbilityButtons(this)); }
    });
}

// Page-load mount once the DOM is ready (no-op on pages with no pane). AJAX side
// panels / sheet refreshes call window.ftMountPcSheet() explicitly after injection.
(function ftInitPcSheet(): void {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => ftMountPcSheet());
    } else {
        queueMicrotask(() => ftMountPcSheet());
    }
})();
